import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

interface Tenant {
  id: string
  name: string
  plan: string
  status: string
  internal: boolean
  country: string | null
  createdAt: string
  _count: { members: number; sites: number }
}

export function AdminTenantsPage() {
  const t = useT()
  const [q, setQ] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const tenants = useQuery({
    queryKey: ['admin-tenants', q, planFilter, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      if (planFilter) params.set('plan', planFilter)
      if (statusFilter) params.set('status', statusFilter)
      return apiClient.get<{ data: Tenant[]; nextCursor: string | null }>(`/admin/tenants?${params}`)
    },
  })

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">{t('admin.tenants.title')}</h1>

      <div className="flex gap-2 mb-4 flex-wrap">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('admin.tenants.search')}
          className="px-2 py-1 text-sm border rounded bg-transparent"
          style={{ borderColor: 'var(--color-border)' }}
        />
        <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className="px-2 py-1 text-sm border rounded bg-transparent" style={{ borderColor: 'var(--color-border)' }}>
          <option value="">{t('admin.tenants.allplans')}</option>
          <option value="starter">starter</option>
          <option value="pro">pro</option>
          <option value="business">business</option>
          <option value="enterprise">enterprise</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-2 py-1 text-sm border rounded bg-transparent" style={{ borderColor: 'var(--color-border)' }}>
          <option value="">{t('admin.tenants.allstatus')}</option>
          <option value="trial">trial</option>
          <option value="active">active</option>
          <option value="past_due">past_due</option>
          <option value="quarantined">quarantined</option>
          <option value="cancelled">cancelled</option>
        </select>
      </div>

      {tenants.isLoading && <p>{t('common.loading')}</p>}
      {tenants.isError && <p>{t('common.error')}</p>}
      {tenants.data && (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left opacity-70">
              <th className="py-2 pr-4">{t('admin.tenants.column.name')}</th>
              <th className="py-2 pr-4">{t('admin.tenants.column.plan')}</th>
              <th className="py-2 pr-4">{t('admin.tenants.column.status')}</th>
              <th className="py-2 pr-4">{t('admin.tenants.column.internal')}</th>
              <th className="py-2 pr-4">{t('admin.tenants.column.members')}</th>
              <th className="py-2 pr-4">{t('admin.tenants.column.sites')}</th>
              <th className="py-2 pr-4">{t('admin.tenants.column.created')}</th>
            </tr>
          </thead>
          <tbody>
            {tenants.data.data.map((row) => (
              <tr key={row.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                <td className="py-2 pr-4">
                  <Link to={row.id} className="hover:underline font-medium">{row.name}</Link>
                  <div className="text-xs opacity-50 font-mono">{row.id.slice(0, 8)}…</div>
                </td>
                <td className="py-2 pr-4">{row.plan}</td>
                <td className="py-2 pr-4">{row.status}</td>
                <td className="py-2 pr-4">{row.internal ? '✓' : ''}</td>
                <td className="py-2 pr-4">{row._count.members}</td>
                <td className="py-2 pr-4">{row._count.sites}</td>
                <td className="py-2 pr-4">{new Date(row.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
